import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import MoneyComponent from '../../elements/MoneyComponent.jsx';
import MaterialIcon from '../../elements/MaterialIcon.jsx';
import { fetchAccountTransactions } from '../../../redux/actions/accounts.js';

/** How a transaction is matched against another account's: the same day and the same amount, as the merge preview counts duplicates. */
export function transactionMatchKey(transaction) {
	return `${transaction.date}|${transaction.amount}`;
}

/**
 * Loads an account's newest transactions. `accountId` null loads nothing. An answer for an account
 * no longer asked about is dropped.
 */
export function useAccountTransactions(accountId, limit = 10) {
	const dispatch = useDispatch();
	const [state, setState] = useState({ loading: false, error: null, data: null });

	useEffect(() => {
		if(accountId === null || accountId === undefined) {
			setState({ loading: false, error: null, data: null });
			return undefined;
		}
		let current = true;
		setState({ loading: true, error: null, data: null });
		dispatch(fetchAccountTransactions(accountId, limit))
			.then((data) => {
				if(current) {
					setState({ loading: false, error: null, data });
				}
			})
			.catch((message) => {
				if(current) {
					setState({ loading: false, error: typeof message === 'string' ? message : 'The transactions could not be loaded.', data: null });
				}
			});
		return () => {
			current = false;
		};
	}, [accountId, limit]);

	return state;
}

function formatDay(day) {
	return moment(day, 'YYYY-MM-DD').format('MMM D, YYYY');
}

/**
 * A short list of an account's newest transactions, and how many there are in all. A row whose
 * key is in `matches` is marked as also being on the other account being compared. `note` replaces
 * the line under the list, for a list drawn from something other than the account's whole history.
 */
export function AccountTransactionList({ loading, error, data, matches = null, matchTitle = '', note = null }) {
	if(loading) {
		return <p className='account_edit_hint account_transactions_loading'>Loading transactions…</p>;
	}
	if(error) {
		return <p className='account_edit_error red-text' role='alert'>{error}</p>;
	}
	if(!data) {
		return null;
	}
	if(data.count === 0) {
		return <p className='account_edit_hint account_transactions_none'>No transactions.</p>;
	}
	const range = data.first === data.last ? formatDay(data.first) : `${formatDay(data.first)} – ${formatDay(data.last)}`;
	return (
		<div className='account_transactions'>
			<ul className='account_transactions_list'>
				{data.transactions.map((transaction) => {
					const matched = matches && matches.has(transactionMatchKey(transaction));
					return (
						<li
							key={transaction.id}
							className={'account_transaction' + (matched ? ' matches_other' : '')}
							title={matched ? matchTitle : undefined}
						>
							<span className='account_transaction_date'>{moment(transaction.date, 'YYYY-MM-DD').format('MMM D, YY')}</span>
							<span className='account_transaction_name'>
								{transaction.name || 'No description'}
								{transaction.pending && <span className='account_transaction_pending'> pending</span>}
							</span>
							<span className='account_transaction_amount'>
								{transaction.amount === null
									? '—'
									: <MoneyComponent amount={transaction.amount} showPositive={false} showNegative={false} />}
							</span>
						</li>
					);
				})}
			</ul>
			<p className='account_edit_hint account_transactions_summary'>
				{note !== null ? note : data.count > data.transactions.length
					? `The newest ${data.transactions.length} of ${data.count}, ${range}.`
					: `${data.count === 1 ? 'The only one' : `All ${data.count}`}, ${range}.`}
			</p>
		</div>
	);
}

/** The editor's section: shut until asked for, then the account's newest transactions. */
export default function AccountTransactions({ accountId, limit = 10 }) {
	const [open, setOpen] = useState(false);
	const state = useAccountTransactions(open ? accountId : null, limit);
	return (
		<div className='account_edit_field account_transactions_field'>
			<button
				type='button'
				className='btn-flat account_transactions_toggle'
				aria-expanded={open}
				onClick={() => setOpen((current) => !current)}
			>
				<MaterialIcon>{open ? 'expand_less' : 'expand_more'}</MaterialIcon>
				{open ? 'Hide transactions' : 'Show recent transactions'}
			</button>
			{open && <AccountTransactionList {...state} />}
		</div>
	);
}
